import * as Sentry from "@sentry/react";
import { Permissions } from "@cnx/common";
import packageJson from "src/../../../package.json";

const toNumber = (value: string | undefined, fallback: number): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const sanitizeBreadcrumbs = <T extends Sentry.Event>(event: T): T => {
  if (!event.breadcrumbs) return event;

  if (event.breadcrumbs.length > 50) {
    event.breadcrumbs = event.breadcrumbs.slice(-50);
  }

  event.breadcrumbs = event.breadcrumbs.map((crumb: Sentry.Breadcrumb) => {
    if (!crumb?.data || typeof crumb.data !== "object") return crumb;
    const data: Record<string, unknown> = { ...(crumb.data as any) };

    if (typeof (data as any).data === "string") {
      const v = (data as any).data as string;
      if (v.length > 5000 || v.startsWith("data:application/octet-stream")) {
        delete (data as any).data;
      }
    }

    for (const key of Object.keys(data)) {
      const value = data[key];
      if (typeof value === "string" && value.length > 1000) {
        data[key] = "[TRUNCATED large breadcrumb field]";
      }
    }

    return { ...crumb, data };
  });

  return event;
};

const dropIfTooBig = <T extends Sentry.Event>(event: T): T | null => {
  try {
    if (JSON.stringify(event).length > 800_000) return null;
  } catch {}
  return event;
};

class SentryService {
  private isSentryLoaded = false;
  private sentryEnv = {
    enabled: import.meta.env.VITE_SENTRY_ENABLED === "true",
    dsn: import.meta.env.VITE_SENTRY_DSN || "",
    nodeEnv: import.meta.env.VITE_NODE_ENV || "development",
    tracesSampleRate: toNumber(
      import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
      0.2
    ),
    replaySessionSampleRate: toNumber(
      import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE,
      0.1
    ),
    errorSampleRate: toNumber(
      import.meta.env.VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE,
      1.0
    ),
    telemetry: import.meta.env.VITE_SENTRY_TELEMETRY === "true",
  };

  init = () => {
    if (!this.sentryEnv.enabled || !this.sentryEnv.dsn) {
      console.warn("Sentry not enabled", this.sentryEnv);
      return;
    }

    Sentry.init({
      dsn: this.sentryEnv.dsn,
      environment: this.sentryEnv.nodeEnv,
      release: `JAPP@${packageJson.version}`,
      tracesSampleRate: this.sentryEnv.tracesSampleRate,
      replaysSessionSampleRate: this.sentryEnv.replaySessionSampleRate,
      replaysOnErrorSampleRate: this.sentryEnv.errorSampleRate,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({ networkCaptureBodies: false }),
      ],
      transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

      beforeBreadcrumb(breadcrumb: Sentry.Breadcrumb) {
        const data = breadcrumb.data as any;
        const url = data?.url as string | undefined;

        if (
          (breadcrumb.category === "xhr" || breadcrumb.category === "fetch") &&
          url?.includes("/socket.io/")
        ) {
          return null;
        }

        if (data && typeof data === "object" && typeof data.data === "string") {
          const v = data.data as string;
          if (
            v.startsWith("data:application/octet-stream") ||
            v.length > 5000
          ) {
            delete data.data;
          }
        }

        return breadcrumb;
      },

      beforeSend(event) {
        event = sanitizeBreadcrumbs(event);

        if (
          event.request?.data &&
          typeof event.request.data === "string" &&
          event.request.data.length > 2000
        ) {
          event.request.data = "[TRUNCATED request.data > 2000 chars]";
        }

        return dropIfTooBig(event);
      },

      beforeSendTransaction(transaction) {
        transaction.spans?.forEach(span => {
          if (span.data) {
            span.data = {};
          }
        });

        if (transaction.spans && transaction.spans.length > 80) return null;

        transaction = sanitizeBreadcrumbs(transaction);
        return dropIfTooBig(transaction);
      },

      normalizeDepth: 3,
    });

    this.isSentryLoaded = true;
  };

  setUser = (user?: {
    id: string;
    email?: string;
    fullName?: string;
    useRights?: Permissions[];
    avatar?: string;
  }) => {
    if (!this.isSentryLoaded) {
      return;
    }

    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.fullName,
        useRights: user.useRights,
      });
    } else {
      Sentry.setUser(null);
    }
  };
}

export default new SentryService();

export const SentryErrorBoundary = Sentry.ErrorBoundary;
